import React, { useState } from 'react';
import { type IEvent } from '../../reducers/eventsSlice';
import { UserGroupIcon } from '@heroicons/react/20/solid';

interface EventAttendeeAllocationGaugeProps {
  event: IEvent;
}

const calculateAccountsAllocatedPercentage = (childAccounts: number, poolSize: number): number => {
  if (poolSize === 0) return 0;
  return (childAccounts / poolSize) * 100;
};

const EventAttendeeAllocationGauge: React.FC<EventAttendeeAllocationGaugeProps> = ({ event }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const accountPool = event.account_pool;
  const totalReadyAccounts = event.account_pool?.total_ready_child_accounts ?? 0;
  const childAccounts = accountPool?.total_allocated_child_accounts ?? 0;
  const accountsAllocatedPercentage = calculateAccountsAllocatedPercentage(childAccounts, totalReadyAccounts);

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        setShowTooltip(true);
      }}
      onMouseLeave={() => {
        setShowTooltip(false);
      }}
    >
      {/* Circular gauge */}
      <div className="relative w-12 h-12">
        {/* Background circle */}
        <svg className="w-12 h-12 transform rotate-[150deg]" viewBox="0 0 48 48">
          <circle
            cx="24"
            cy="24"
            r="20"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            className="text-dataops-rich-black/10"
            strokeDasharray={`${2 * Math.PI * 20 * 0.67} ${2 * Math.PI * 20}`}
            strokeDashoffset="0"
          />
          {/* Progress circle */}
          <circle
            cx="24"
            cy="24"
            r="20"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            strokeDasharray={`${2 * Math.PI * 20 * 0.67 * (accountsAllocatedPercentage / 100)} ${2 * Math.PI * 20}`}
            strokeDashoffset="0"
            className="text-dataops-yale-blue transition-all duration-300"
            strokeLinecap="round"
          />
        </svg>
        {/* UserGroup icon in center */}
        <div className="absolute inset-0 flex items-center justify-center">
          <UserGroupIcon className="w-5 h-5 text-dataops-yale-blue" />
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute bottom-14 left-1/2 transform -translate-x-1/2 bg-dataops-rich-black text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
          <div className="font-medium">Attendee Allocation</div>
          <div>
            {childAccounts} / {totalReadyAccounts} ({Math.round(accountsAllocatedPercentage)}%)
          </div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-dataops-rich-black"></div>
        </div>
      )}
    </div>
  );
};

export default EventAttendeeAllocationGauge;
